import React from 'react';
import defaultTo from 'lodash/defaultTo';
import { EntitlementDetails } from '@css-xp/graphtypes/src/generated-types-new';
import { Field, FormFooter } from '@atlaskit/form';
import Button, { ButtonGroup, LoadingButton } from '@atlaskit/button';
import classNames from 'classnames';
import Textfield from '@atlaskit/textfield';
import DoneIcon from '@atlaskit/icon/glyph/editor/done';
import InfoIcon from '@atlaskit/icon/glyph/info';
import Tooltip from '@atlaskit/tooltip';
import Checkbox from '@atlaskit/checkbox';
import { updateInvoiceGroup } from '@atlassian/temp-proxies';
import SectionMessage from '@atlaskit/section-message';

interface POUpdateProps {
  setCanUpdatePO: React.Dispatch<React.SetStateAction<boolean>>;
  entitlement: EntitlementDetails;
  shipToPartyId?: string | undefined;
  billingGroupId?: string;
}

type PORequest = {
  loading: boolean;
  data: any;
  done: boolean;
  error: any;
};

// TODO: Move to common components
export const POUpdate = ({ setCanUpdatePO, entitlement, shipToPartyId, billingGroupId = '' }: POUpdateProps) => {
  const [isPOUpdated, setIsPOUpdated] = React.useState<Partial<PORequest>>({ error: null });
  const [isMultiPO, setIsMultiPO] = React.useState<boolean>(false);
  const [purchaseOrder, setPurchaseOrder] = React.useState<string>('');

  const onSubmitPOUpdate = async () => {
    if (purchaseOrder.trim().length === 0) return;
    const transactionAccountId = defaultTo(entitlement?.transactionAccountId, '');
    const invoiceGroupId = defaultTo(entitlement?.billingGroupId, billingGroupId);
    try {
      setIsPOUpdated({ loading: true, data: null, done: false, error: null });
      const response = await updateInvoiceGroup({
        transactionAccountId,
        invoiceGroupId,
        data: {
          shipToPartyId,
          purchaseOrder: {
            number: purchaseOrder,
            oneTimeUse: !isMultiPO,
          },
        },
      });
      setIsPOUpdated({ loading: false, data: response, done: true, error: null });
    } catch (error) {
      setIsPOUpdated({ loading: false, data: null, done: false, error });
    }
  };

  const onChangePurchaseOrder = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsPOUpdated({ loading: false, data: null, done: false, error: null });
    setPurchaseOrder(event.target.value);
  };

  return (
    <div className="po-section">
      <Field label={'PO number'} name="ponumber">
        {({ fieldProps }) => (
          <Textfield
            {...fieldProps}
            onChange={onChangePurchaseOrder}
            value={purchaseOrder}
            name={'ponumber'}
            testId="ponumber-field"
          />
        )}
      </Field>
      <div className="elem-with-tooltip">
        <Checkbox
          isChecked={isMultiPO}
          onChange={() => setIsMultiPO(!isMultiPO)}
          label="Multi-use PO number"
          value="Multi-use PO number"
          name="Multi-use PO number"
          testId="multi-use-po-number"
          alt="multi-use PO number"
        />
        <Tooltip content="Selecting the multi-use option means the purchase order number will be reflected in all subsequent invoices">
          <InfoIcon label="more-information" size="small" />
        </Tooltip>
      </div>
      {isPOUpdated.error !== null && (
        <SectionMessage appearance="error">
          <strong>The PO number didn’t update - try again.</strong>
        </SectionMessage>
      )}
      <FormFooter align="end">
        <ButtonGroup>
          <Button testId="cancel-po-btn" className="undo-btn" appearance="link" onClick={() => setCanUpdatePO(false)}>
            Undo
          </Button>
          <LoadingButton
            aria-label="Update PO number"
            className={classNames({ 'update-done': isPOUpdated.done })}
            iconBefore={isPOUpdated.done ? <DoneIcon testId="done" label="done" /> : undefined}
            onClick={!isPOUpdated.done ? onSubmitPOUpdate : undefined}
            appearance="primary"
            isLoading={isPOUpdated.loading}
            testId="update-po-btn"
          >
            {isPOUpdated.done ? 'Updated' : 'Update'}
          </LoadingButton>
        </ButtonGroup>
      </FormFooter>
    </div>
  );
};
